import React, { useEffect, useState } from "react";
import { api, formatCHF, currentMonthString, formatMonat } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  monat: currentMonthString(),
  tour: "",
  picks_atz_blick_woz: 0,
  preis_pro_pick_atz: 0,
  picks_vdl: 0,
  preis_pro_pick_vdl: 0,
  bemerkung: "",
};

export default function EPK() {
  const [monat, setMonat] = useState(currentMonthString());
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY, monat });

  const load = async () => {
    const r = await api.get("/epk", { params: { monat } });
    setList(r.data);
  };
  useEffect(() => { load(); setForm((f) => ({ ...f, monat })); }, [monat]);

  const umsatz = (Number(form.picks_atz_blick_woz) || 0) * (Number(form.preis_pro_pick_atz) || 0) + (Number(form.picks_vdl) || 0) * (Number(form.preis_pro_pick_vdl) || 0);

  const save = async () => {
    try {
      const payload = {
        ...form,
        picks_atz_blick_woz: Number(form.picks_atz_blick_woz) || 0,
        picks_vdl: Number(form.picks_vdl) || 0,
        preis_pro_pick_atz: Number(form.preis_pro_pick_atz) || 0,
        preis_pro_pick_vdl: Number(form.preis_pro_pick_vdl) || 0,
      };
      if (editing) await api.put(`/epk/${editing.id}`, payload);
      else await api.post("/epk", payload);
      toast.success("Gespeichert");
      setOpen(false); load();
    } catch { toast.error("Fehler"); }
  };

  return (
    <PageContainer>
      <PageHeader title="EPK / Picks" subtitle="Monatliche Pick-Erfassung" testId="epk-page">
        <MonthPicker value={monat} onChange={setMonat} testId="epk-month-picker" />
        <Button onClick={() => { setEditing(null); setForm({ ...EMPTY, monat }); setOpen(true); }} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="add-epk-button">
          <Plus className="h-4 w-4 mr-2" /> Neuer Eintrag
        </Button>
      </PageHeader>

      <Card className="border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Monat</TableHead><TableHead>Tour</TableHead>
                <TableHead className="text-right">Picks ATZ/Blick/WoZ</TableHead><TableHead className="text-right">Preis/Pick</TableHead>
                <TableHead className="text-right">Picks VDL</TableHead><TableHead className="text-right">Preis/Pick</TableHead>
                <TableHead className="text-right">Umsatz</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (<TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-8">Keine Einträge.</TableCell></TableRow>)}
              {list.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="font-mono">{formatMonat(e.monat)}</TableCell>
                  <TableCell className="font-medium">{e.tour}</TableCell>
                  <TableCell className="nivara-num">{e.picks_atz_blick_woz}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(e.preis_pro_pick_atz)}</TableCell>
                  <TableCell className="nivara-num">{e.picks_vdl}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(e.preis_pro_pick_vdl)}</TableCell>
                  <TableCell className="nivara-num font-semibold">{formatCHF(e.umsatz)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(e); setForm({ ...EMPTY, ...e }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={async () => { if(window.confirm("Löschen?")){ await api.delete(`/epk/${e.id}`); load();}}}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "EPK bearbeiten" : "Neuer EPK-Eintrag"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Monat</Label><Input type="month" value={form.monat} onChange={(e) => setForm({...form, monat: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Tour</Label><Input value={form.tour} onChange={(e) => setForm({...form, tour: e.target.value})} /></div>
            <div className="space-y-2"><Label>Picks ATZ/Blick/WoZ</Label><Input type="number" value={form.picks_atz_blick_woz} onChange={(e) => setForm({...form, picks_atz_blick_woz: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Preis pro Pick (CHF)</Label><Input type="number" step="0.01" value={form.preis_pro_pick_atz} onChange={(e) => setForm({...form, preis_pro_pick_atz: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Picks VDL</Label><Input type="number" value={form.picks_vdl} onChange={(e) => setForm({...form, picks_vdl: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Preis pro Pick (CHF)</Label><Input type="number" step="0.01" value={form.preis_pro_pick_vdl} onChange={(e) => setForm({...form, preis_pro_pick_vdl: e.target.value})} className="font-mono" /></div>
            <Card className="sm:col-span-2 bg-neutral-50 p-3 border-neutral-200">
              <div className="text-xs text-neutral-500 uppercase tracking-wider">Berechneter Umsatz</div>
              <div className="font-mono font-semibold text-lg">{formatCHF(umsatz)}</div>
            </Card>
            <div className="space-y-2 sm:col-span-2"><Label>Bemerkung</Label><Input value={form.bemerkung || ""} onChange={(e) => setForm({...form, bemerkung: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0040CC]">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
